import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, Clock, Star, Plus, LogOut, ChevronRight, Loader2, User, Shield,
  FileText, Trash2, ExternalLink, Sparkles, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeIn = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };
const stagger = { animate: { transition: { staggerChildren: 0.06 } } };

const ROLE_LABELS = {
  user: "MyGoals User",
  admin: "MyGoals Admin",
  microsoft_manager: "Microsoft Manager",
  super_admin: "Super Admin",
};

const ROLE_COLORS = {
  user: "bg-[#B9DCD2]/30 text-[#194449]",
  admin: "bg-[#D4EC8E]/30 text-[#3d5a00]",
  microsoft_manager: "bg-blue-100 text-blue-700",
  super_admin: "bg-amber-100 text-amber-700",
};

const FORMAT_COLORS = {
  Course: "bg-blue-50 text-blue-700",
  Certification: "bg-amber-50 text-amber-700",
  "Applied Skill": "bg-purple-50 text-purple-700",
  Guide: "bg-emerald-50 text-emerald-700",
};

export default function MyPlansPage() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latestResources, setLatestResources] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [plansRes, latestRes] = await Promise.all([
        axios.get(`${API}/auth/my-plans`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/resources/latest?limit=8`),
      ]);
      setPlans(plansRes.data.plans || []);
      setLatestResources(latestRes.data || []);
    } catch {
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Delete this goal? This cannot be undone.")) return;
    setDeletingId(sessionId);
    try {
      await axios.delete(`${API}/auth/my-plans/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlans(prev => prev.filter(p => p.session_id !== sessionId));
    } catch {
      alert("Failed to delete goal");
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  const role = user?.role || "user";
  const hasAdminAccess = ["admin", "microsoft_manager", "super_admin"].includes(role);

  return (
    <div className="min-h-screen bg-[#FFF8F3]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-[#194449] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="logo-home-link-myplans">
              <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-6 brightness-0 invert" />
              <div className="w-px h-6 bg-white/30" />
              <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                MyGoals
              </span>
            </button>
          </div>
          <div className="flex items-center gap-4">
            {hasAdminAccess && (
              <button onClick={() => navigate("/portal")} className="text-sm text-white/70 hover:text-white transition-colors hidden md:block" data-testid="portal-link-myplans">
                Manager Portal
              </button>
            )}
            {hasAdminAccess && (
              <button onClick={() => navigate("/admin")} className="text-sm text-white/50 hover:text-white/80 transition-colors hidden md:block" data-testid="admin-link-myplans">
                Admin
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-white/90 hidden md:inline">
                {user?.first_name || user?.email?.split("@")[0]}
              </span>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <motion.div initial="initial" animate="animate" variants={stagger} className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {/* User Profile + New Goal */}
        <motion.div variants={fadeIn} className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#194449] flex items-center justify-center">
                <span className="text-xl font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {(user?.first_name?.[0] || user?.email?.[0] || "U").toUpperCase()}
                </span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="user-display-name">
                  {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.email}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[role] || ROLE_COLORS.user}`} data-testid="user-role-badge">
                    <Shield className="w-3 h-3" />
                    {ROLE_LABELS[role] || role}
                  </span>
                  {user?.domain_type && <span className="text-xs text-[#94A3B8]">{user.domain_type}</span>}
                </div>
              </div>
            </div>
            <Button onClick={() => navigate("/intake")}
              className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-6 h-11 font-medium shadow-md hover:shadow-lg transition-all duration-300"
              data-testid="new-goal-btn">
              <Plus className="w-4 h-4 mr-2" /> New Goal
            </Button>
          </div>
        </motion.div>

        {/* My Saved Goals */}
        <motion.div variants={fadeIn} className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="my-goals-heading">
              <FileText className="w-5 h-5 inline-block mr-2 text-[#194449] -mt-0.5" />
              My Saved Goals
            </h2>
            <span className="text-sm text-[#94A3B8]" data-testid="goals-count">
              {plans.length} {plans.length === 1 ? "goal" : "goals"}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#194449]" />
            </div>
          ) : plans.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-12 text-center" data-testid="no-goals-message">
              <BookOpen className="w-12 h-12 text-[#B9DCD2] mx-auto mb-4" strokeWidth={1.2} />
              <h3 className="text-base font-semibold text-[#091F2C] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                No saved goals yet
              </h3>
              <p className="text-sm text-[#64748B] mb-6 max-w-sm mx-auto">
                Create your first learning goal and start building your personalized skill path.
              </p>
              <Button onClick={() => navigate("/intake")}
                className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-8 h-11 font-medium"
                data-testid="create-first-goal-btn">
                Create My First Goal
              </Button>
            </div>
          ) : (
            <motion.div variants={stagger} className="grid gap-3">
              {plans.map((plan) => (
                <motion.div key={plan.session_id} variants={fadeIn}
                  className="bg-white rounded-xl border border-black/5 shadow-sm p-5 hover:border-[#194449]/20 hover:shadow-md transition-all duration-200 group"
                  data-testid={`goal-card-${plan.session_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/plan/${plan.session_id}`)}>
                      <h3 className="text-base font-medium text-[#091F2C] truncate group-hover:text-[#194449] transition-colors" data-testid="goal-title">
                        {plan.title}
                      </h3>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1.5 text-xs text-[#94A3B8]">
                          <BookOpen className="w-3.5 h-3.5" /> {plan.resource_count} resources
                        </span>
                        {plan.star_rating && (
                          <span className="flex items-center gap-1 text-xs text-amber-500">
                            <Star className="w-3.5 h-3.5 fill-current" /> {plan.star_rating}/5
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 text-xs text-[#94A3B8]">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(plan.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#CBD5E1] hover:text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(plan.session_id)}
                        disabled={deletingId === plan.session_id}
                        data-testid={`delete-goal-${plan.session_id}`}>
                        {deletingId === plan.session_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                      <ChevronRight className="w-5 h-5 text-[#CBD5E1] group-hover:text-[#194449] transition-colors cursor-pointer"
                        onClick={() => navigate(`/plan/${plan.session_id}`)} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.div>

        {/* New on LevelUp */}
        {latestResources.length > 0 && (
          <motion.div variants={fadeIn}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="new-on-levelup-heading">
                <Sparkles className="w-5 h-5 inline-block mr-2 text-[#07641D] -mt-0.5" />
                New on LevelUp
              </h2>
              <span className="text-xs text-[#94A3B8]">Recently added courses</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {latestResources.map((r) => (
                <div key={r.resource_id}
                  className="bg-white rounded-xl border border-black/5 shadow-sm p-4 hover:border-[#07641D]/20 hover:shadow-md transition-all duration-200 group"
                  data-testid={`latest-resource-${r.resource_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        {r.content_format && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${FORMAT_COLORS[r.content_format] || "bg-gray-100 text-gray-600"}`}>
                            {r.content_format}
                          </span>
                        )}
                        {r.skill_level && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4F3F5] text-[#64748B]">{r.skill_level}</span>
                        )}
                      </div>
                      <h4 className="text-sm font-medium text-[#091F2C] leading-snug line-clamp-2 group-hover:text-[#194449] transition-colors">
                        {r.title}
                      </h4>
                      {r.hero_products?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {r.hero_products.slice(0, 2).map(p => (
                            <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[#194449]/8 text-[#194449]">
                              {p.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {r.url && (
                        <a href={r.url} target="_blank" rel="noopener noreferrer"
                          className="text-[#94A3B8] hover:text-[#194449] transition-colors" title="View course">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <Button onClick={() => navigate("/intake")}
                variant="outline" className="rounded-full px-8 h-10 border-[#194449]/20 text-[#194449] hover:bg-[#194449] hover:text-white transition-all"
                data-testid="build-goal-from-courses-btn">
                Build a goal around these courses <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
